import { auth, db } from '../firebase';
import { deleteDoc, doc, setDoc } from 'firebase/firestore';

// A saved vocab word, story, or conversation line. Denormalized on purpose —
// the Bookmarks page renders straight from this, no refetch of the original
// content JSON. Mirrors mobile's BookmarksService/BookmarkItem
// (lib/services/bookmarks_service.dart).
//
// BookmarkItem shape: { id, type, langPair, nativeText, targetText, imageName, emoji, savedAt }
// - id is caller-constructed (e.g. `vocab_${nativeCode}_${learnCode}_${category}_${wordKey}`);
//   this service just stores/looks up whatever id string it's given.

const PREFIX = 'bookmarks_';

function storageKey(langPair) {
  return `${PREFIX}${langPair}`;
}

function readAll(langPair) {
  try {
    const raw = localStorage.getItem(storageKey(langPair));
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeAll(langPair, items) {
  localStorage.setItem(storageKey(langPair), JSON.stringify(items));
}

export const BookmarksService = {
  all(langPair) {
    return readAll(langPair)
      .slice()
      .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime());
  },

  isBookmarked(langPair, id) {
    return readAll(langPair).some((b) => b.id === id);
  },

  /**
   * Adds `item` to the front of the list if not already saved, removes it
   * otherwise. Returns true if it was just added, false if it was just
   * removed. Persists to localStorage and best-effort syncs to Firestore at
   * users/{uid}/bookmarks/{item.id} when signed in.
   */
  toggle(item) {
    const items = readAll(item.langPair);
    const existingIdx = items.findIndex((b) => b.id === item.id);
    const adding = existingIdx < 0;

    let next;
    let saved;
    if (adding) {
      saved = { ...item, savedAt: item.savedAt || new Date().toISOString() };
      next = [saved, ...items];
    } else {
      saved = items[existingIdx];
      next = items.filter((_, i) => i !== existingIdx);
    }
    writeAll(item.langPair, next);

    const uid = auth?.currentUser?.uid;
    if (uid && db) {
      if (adding) {
        setDoc(doc(db, 'users', uid, 'bookmarks', item.id), saved)
          .catch((e) => console.warn('BookmarksService: sync failed', e));
      } else {
        deleteDoc(doc(db, 'users', uid, 'bookmarks', item.id))
          .catch((e) => console.warn('BookmarksService: delete failed', e));
      }
    }
    return adding;
  },

  remove(item) {
    if (this.isBookmarked(item.langPair, item.id)) {
      this.toggle(item);
    }
  },
};
